import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { t } from "@lingui/macro";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowsClockwise,
  ArrowUp,
  CaretRight,
  CopySimple,
  DotsSixVertical,
  DotsThree,
  Eye,
  EyeSlash,
  PencilSimple,
  Plus,
  TrashSimple,
  Warning,
} from "@phosphor-icons/react";
import type { MyMindItem } from "@stylingcv/schema";
import { cn } from "@stylingcv/utils";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useDialog } from "@/client/stores/dialog";
import { useResumeStore } from "@/client/stores/resume";

import { SectionIcon } from "./shared/section-icon";
import { SectionOptions } from "./shared/section-options";

// ─── Palette ───────────────────────────────────────────────────────────────────

const SEGMENT_COLORS = [
  "#005f73",
  "#0a9396",
  "#94d2bd",
  "#e9d8a6",
  "#ee9b00",
  "#ca6702",
  "#bb3e03",
  "#ae2012",
];

const getItemColor = (item: MyMindItem, index: number) =>
  (item as any).color || SEGMENT_COLORS[index % SEGMENT_COLORS.length];

// ─── Settings Panel (light design) ───────────────────────────────────────────

type SettingsPanelProps = {
  ringThickness: number;
  showLegend: boolean;
  legendPlacement: "top" | "bottom" | "right" | "left";
  onThicknessChange: (v: number) => void;
  onLegendChange: (v: boolean) => void;
  onPlacementChange: (v: "top" | "bottom" | "right" | "left") => void;
};

const THICKNESS_OPTIONS: { label: string; value: number }[] = [
  { label: "S", value: 1 },
  { label: "M", value: 2 },
  { label: "L", value: 3 },
  { label: "XL", value: 5 },
];

const SettingsPanel = ({
  ringThickness,
  showLegend,
  legendPlacement,
  onThicknessChange,
  onLegendChange,
  onPlacementChange,
}: SettingsPanelProps) => {
  const placements: { value: "top" | "bottom" | "right" | "left"; icon: React.ReactNode }[] = [
    { value: "top", icon: <ArrowUp size={13} /> },
    { value: "right", icon: <ArrowRight size={13} /> },
    { value: "bottom", icon: <ArrowDown size={13} /> },
    { value: "left", icon: <ArrowLeft size={13} /> },
  ];

  const activeBtnClass = "bg-[#268599] text-white border-[#268599]";
  const idleBtnClass =
    "border border-[var(--border,#e5e5e5)] bg-white text-gray-700 hover:border-[#268599] hover:text-[#268599]";

  return (
    <div className="space-y-3 rounded-xl border border-[var(--border,#e5e5e5)] bg-white p-4">
      <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
        {t`Chart Settings`}
      </p>

      {/* Ring thickness */}
      <div className="flex flex-wrap items-center gap-3">
        <span className="w-32 shrink-0 text-[12px] text-gray-600">{t`Ring thickness`}</span>
        <div className="flex gap-1">
          {THICKNESS_OPTIONS.map(({ label, value }) => (
            <button
              key={value}
              className={cn(
                "h-8 min-w-[2rem] rounded-lg px-2.5 text-[12px] font-semibold transition-colors",
                ringThickness === value ? activeBtnClass : idleBtnClass,
              )}
              onClick={() => onThicknessChange(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Show legend */}
      <div className="flex flex-wrap items-center gap-3">
        <span className="w-32 shrink-0 text-[12px] text-gray-600">{t`Show legend`}</span>
        <div className="flex gap-1">
          {[
            { label: t`Yes`, value: true },
            { label: t`No`, value: false },
          ].map(({ label, value }) => (
            <button
              key={String(value)}
              className={cn(
                "h-8 rounded-lg px-3 text-[12px] font-semibold transition-colors",
                showLegend === value ? activeBtnClass : idleBtnClass,
              )}
              onClick={() => onLegendChange(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Legend position */}
      {showLegend && (
        <div className="flex flex-wrap items-center gap-3">
          <span className="w-32 shrink-0 text-[12px] text-gray-600">{t`Legend position`}</span>
          <div className="flex gap-1">
            {placements.map(({ value, icon }) => (
              <button
                key={value}
                title={value}
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-lg transition-colors",
                  legendPlacement === value ? activeBtnClass : idleBtnClass,
                )}
                onClick={() => onPlacementChange(value)}
              >
                {icon}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── MyMind List Item ─────────────────────────────────────────────────────────

type MyMindListItemProps = {
  item: MyMindItem;
  index: number;
  isDragOverlay?: boolean;
  onUpdate: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onToggleVisibility: () => void;
};

const MyMindListItem = ({
  item,
  index,
  isDragOverlay,
  onUpdate,
  onDuplicate,
  onDelete,
  onToggleVisibility,
}: MyMindListItemProps) => {
  const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({
    id: item.id,
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const color = getItemColor(item, index);
  const visible = item.visible ?? true;

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  return (
    <div
      ref={isDragOverlay ? undefined : setNodeRef}
      style={isDragOverlay ? undefined : style}
      className={cn("mb-2", isDragging && !isDragOverlay && "opacity-30")}
    >
      <div
        className={cn(
          "group flex cursor-pointer items-center rounded-[12px] border bg-white transition-all duration-150",
          "gap-2 p-3 @[280px]:gap-3 @[280px]:px-4 @[360px]:gap-[14px] @[360px]:px-[18px] @[360px]:py-[14px]",
          !visible && "opacity-65",
          isDragOverlay
            ? "ring-1 shadow-[0_4px_20px_rgba(22,52,76,0.10)] ring-[#268599]/40"
            : "border-[var(--border,#e5e5e5)] hover:-translate-y-px hover:border-[#268599] hover:shadow-[0_2px_8px_rgba(22,52,76,0.08)]",
        )}
        onClick={onUpdate}
      >
        {/* Drag handle */}
        <div
          {...listeners}
          {...attributes}
          role="button"
          aria-label={t`Drag to reorder`}
          className="hidden shrink-0 cursor-grab touch-manipulation opacity-0 transition-opacity duration-150 active:cursor-grabbing group-hover:opacity-100 @[360px]:flex"
          style={{ color: "var(--fg-4, #a3a3a3)" }}
          onClick={(e) => e.stopPropagation()}
        >
          <DotsSixVertical size={12} weight="bold" />
        </div>

        {/* Colored indicator */}
        <span
          className="size-[9px] shrink-0 rounded-[3px]"
          style={{ backgroundColor: color }}
        />

        {/* Meta */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5 leading-tight">
            <span
              className="truncate font-bold"
              style={{ fontSize: "clamp(12px, 3.5cqw, 14px)", color: "var(--fg-1, #171717)" }}
            >
              {item.label || t`(untitled)`}
            </span>
            {item.percentage > 0 && (
              <span className="rounded-full bg-gray-100 px-1.5 py-[2px] font-mono text-[10px] font-semibold text-gray-500">
                {item.percentage}%
              </span>
            )}
          </div>

          {(item as any).description && (
            <p className="mt-[3px] line-clamp-1 text-[11px] @[280px]:text-[12px]" style={{ color: "var(--fg-3, #737373)" }}>
              {(item as any).description}
            </p>
          )}

          {/* Progress bar */}
          {item.percentage > 0 && (
            <div className="mt-[6px] flex items-center gap-2">
              <div className="h-[3px] flex-1 rounded-full" style={{ background: "#ebebeb" }}>
                <div
                  className="h-[3px] rounded-full transition-all duration-300"
                  style={{ width: `${item.percentage}%`, backgroundColor: color }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Right action zone */}
        <div
          ref={menuRef}
          className="relative flex size-5 shrink-0 items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          <CaretRight
            size={13}
            className="absolute transition-opacity duration-150 group-hover:opacity-0"
            style={{ color: "var(--fg-3, #737373)" }}
          />

          <button
            className={cn(
              "absolute flex size-6 items-center justify-center rounded-[6px] transition-all duration-150",
              "pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100",
              menuOpen && "pointer-events-auto opacity-100",
              menuOpen ? "bg-gray-100" : "hover:bg-gray-100",
            )}
            style={{ color: "var(--fg-3, #737373)" }}
            aria-label={t`Item options`}
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
          >
            <DotsThree size={15} weight="bold" />
          </button>

          {menuOpen && (
            <div
              style={{
                position: "absolute",
                top: "calc(100% + 6px)",
                right: 0,
                zIndex: 200,
                background: "#fff",
                color: "var(--fg-1, #171717)",
                borderRadius: 10,
                boxShadow: "0 8px 28px rgba(22,52,76,0.12)",
                border: "1px solid rgba(0,0,0,0.06)",
                padding: 4,
                width: 160,
              }}
            >
              <MenuBtn icon={<PencilSimple size={13} />} label={t`Edit`} onClick={() => { setMenuOpen(false); onUpdate(); }} />
              <MenuBtn icon={visible ? <EyeSlash size={13} /> : <Eye size={13} />} label={visible ? t`Hide` : t`Show`} onClick={() => { setMenuOpen(false); onToggleVisibility(); }} />
              <MenuBtn icon={<CopySimple size={13} />} label={t`Duplicate`} onClick={() => { setMenuOpen(false); onDuplicate(); }} />
              <div style={{ height: 1, background: "var(--border, #e5e5e5)", margin: "4px 6px" }} />
              <MenuBtn danger icon={<TrashSimple size={13} />} label={t`Delete`} onClick={() => { setMenuOpen(false); onDelete(); }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MenuBtn = ({ icon, label, danger, onClick }: { icon: React.ReactNode; label: string; danger?: boolean; onClick: () => void }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      style={{
        display: "flex", alignItems: "center", gap: 8, width: "100%",
        padding: "6px 10px", fontSize: 13, fontWeight: 500, borderRadius: 6, border: "none",
        background: hovered ? (danger ? "rgba(220,38,38,0.05)" : "#f5f5f5") : "transparent",
        color: danger ? (hovered ? "#dc2626" : "var(--fg-3, #737373)") : "var(--fg-1, #171717)",
        cursor: "pointer", transition: "background 0.1s, color 0.1s",
      }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span style={{ flexShrink: 0, color: danger && hovered ? "#dc2626" : "var(--fg-3, #737373)" }}>{icon}</span>
      {label}
    </button>
  );
};

// ─── Main Section Component ───────────────────────────────────────────────────

export const MyMindSection = () => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const { open } = useDialog("myMind");
  const setValue = useResumeStore((state) => state.setValue);

  const section = useResumeStore((state) => {
    try {
      return (
        state.resume?.data?.sections?.myMind ?? {
          id: "myMind",
          name: "Mindset & Strengths",
          visible: true,
          items: [],
          ringThickness: 4,
          showLegend: true,
          legendPlacement: "right" as const,
          icon: "brain",
        }
      );
    } catch {
      return {
        id: "myMind",
        name: "Mindset & Strengths",
        visible: true,
        items: [],
        ringThickness: 4,
        showLegend: true,
        legendPlacement: "right" as const,
        icon: "brain",
      };
    }
  });

  const sensors = useSensors(
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const items: MyMindItem[] = section.items ?? [];
  const ringThickness: number = (section as any).ringThickness ?? 4;
  const showLegend: boolean = (section as any).showLegend ?? true;
  const legendPlacement: "top" | "bottom" | "right" | "left" = (section as any).legendPlacement ?? "right";
  const sectionName: string = section.name || t`Mindset & Strengths`;

  const totalPercentage = items.reduce((sum, item) => sum + (item.percentage ?? 0), 0);
  const isOverLimit = totalPercentage > 100;

  const onDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const onDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);
    setValue("sections.myMind.items", arrayMove(items, oldIndex, newIndex));
  };

  const onDragCancel = () => setActiveId(null);

  const onCreate = () => open("create", { id: "myMind" });
  const onUpdate = (item: MyMindItem) => open("update", { id: "myMind", item });
  const onDuplicate = (item: MyMindItem) => open("duplicate", { id: "myMind", item });
  const onDelete = (item: MyMindItem) => open("delete", { id: "myMind", item });
  const onToggleVisibility = (index: number) => {
    const visible = items[index]?.visible ?? true;
    setValue(`sections.myMind.items[${index}].visible`, !visible);
  };

  const onThicknessChange = (v: number) => setValue("sections.myMind.ringThickness", v);
  const onLegendChange = (v: boolean) => setValue("sections.myMind.showLegend", v);
  const onPlacementChange = (v: "top" | "bottom" | "right" | "left") =>
    setValue("sections.myMind.legendPlacement", v);

  return (
    <div id="myMind" className="flex h-full flex-col overflow-y-auto">
      {/* ── Header ── */}
      <header className="flex items-center gap-3 border-b border-[var(--border,#e5e5e5)] px-6 py-5">
        <div>
          <button
            className="group/iconbtn relative flex size-10 shrink-0 items-center justify-center rounded-xl bg-gray-100 transition-colors hover:bg-gray-200"
            title={t`Section icon`}
          >
            <SectionIcon id="myMind" size={18} className="" />
            <span className="pointer-events-none absolute -bottom-1 -right-1 flex size-[18px] items-center justify-center rounded-full border-2 border-white bg-[#268599] opacity-0 transition-opacity duration-150 group-hover/iconbtn:opacity-100">
              <ArrowsClockwise size={9} weight="bold" color="white" />
            </span>
          </button>
        </div>

        <div className="min-w-0 flex-1">
          {isRenaming ? (
            <input
              autoFocus
              className="w-full border-b-2 border-[#268599] bg-transparent text-2xl font-bold leading-tight text-[var(--fg-1,#171717)] outline-none"
              value={sectionName}
              onChange={(e) => setValue("sections.myMind.name", e.target.value)}
              onBlur={() => setIsRenaming(false)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") setIsRenaming(false); }}
            />
          ) : (
            <h2 className="truncate text-2xl font-bold leading-tight text-[var(--fg-1,#171717)]">
              {sectionName}
            </h2>
          )}
        </div>

        <button
          className="shrink-0 rounded p-1.5 text-gray-400 transition-colors hover:text-gray-600"
          title={t`Rename section`}
          onClick={() => setIsRenaming(true)}
        >
          <PencilSimple size={16} />
        </button>

        <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
          <SectionOptions id="myMind" />
        </div>
      </header>

      {/* ── Items list ── */}
      <main
        className={cn(
          "flex flex-col p-5 transition-opacity @container",
          !section.visible && "opacity-50",
        )}
      >
        {/* Over-limit warning */}
        <AnimatePresence>
          {isOverLimit && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-3 flex items-center gap-2 overflow-hidden rounded-lg border border-yellow-300/60 bg-yellow-50 px-3 py-2 text-[11px] text-yellow-700"
            >
              <Warning size={14} className="shrink-0" />
              <span>
                {t`Total exceeds 100% (${totalPercentage}%). The chart may not render correctly.`}
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Empty state */}
        {items.length === 0 && (
          <button
            className="group flex w-full items-center justify-between rounded-xl border border-dashed border-[var(--border,#e5e5e5)] p-3 transition-colors hover:border-[#268599] focus-visible:outline-none @[280px]:px-4 @[360px]:px-[18px] @[360px]:py-[14px]"
            onClick={onCreate}
          >
            <div className="flex items-center gap-3">
              <span className="flex size-7 items-center justify-center rounded-full bg-[#268599] text-white transition-colors group-hover:bg-[#155469]">
                <Plus size={14} weight="bold" />
              </span>
              <span className="text-[13px] font-medium text-[var(--fg-2,#404040)] transition-colors group-hover:text-[#268599]">
                {t`Add a new strength`}
              </span>
            </div>
            <span className="hidden rounded border border-[var(--border,#e5e5e5)] px-[6px] py-[3px] text-[10px] font-semibold text-[var(--fg-4,#a3a3a3)] @[280px]:inline">
              ⌘N
            </span>
          </button>
        )}

        {/* Sortable list */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToParentElement]}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragCancel={onDragCancel}
        >
          <SortableContext items={items} strategy={verticalListSortingStrategy}>
            <AnimatePresence>
              {items.map((item, index) => (
                <MyMindListItem
                  key={item.id}
                  item={item}
                  index={index}
                  onUpdate={() => onUpdate(item)}
                  onDuplicate={() => onDuplicate(item)}
                  onDelete={() => onDelete(item)}
                  onToggleVisibility={() => onToggleVisibility(index)}
                />
              ))}
            </AnimatePresence>
          </SortableContext>

          {createPortal(
            <DragOverlay
              dropAnimation={{ duration: 200, easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)" }}
            >
              {activeId
                ? (() => {
                    const activeItem = items.find((item) => item.id === activeId);
                    const activeIndex = items.findIndex((item) => item.id === activeId);
                    if (!activeItem) return null;
                    return (
                      <MyMindListItem
                        isDragOverlay
                        item={activeItem}
                        index={activeIndex}
                        onUpdate={() => {}}
                        onDuplicate={() => {}}
                        onDelete={() => {}}
                        onToggleVisibility={() => {}}
                      />
                    );
                  })()
                : null}
            </DragOverlay>,
            document.body,
          )}
        </DndContext>
      </main>

      {/* ── Add button ── */}
      {items.length > 0 && (
        <footer className="p-5 pt-0">
          <button
            className="group flex w-full items-center justify-between rounded-xl border border-dashed border-[var(--border,#e5e5e5)] p-3 transition-colors hover:border-[#268599] focus-visible:outline-none @[280px]:px-4 @[360px]:px-[18px] @[360px]:py-[14px]"
            onClick={onCreate}
          >
            <div className="flex items-center gap-3">
              <span className="flex size-7 items-center justify-center rounded-full bg-[#268599] text-white transition-colors group-hover:bg-[#155469]">
                <Plus size={14} weight="bold" />
              </span>
              <span className="text-[13px] font-medium text-[var(--fg-2,#404040)] transition-colors group-hover:text-[#268599]">
                {t`Add a new strength`}
              </span>
            </div>
            <span className="hidden rounded border border-[var(--border,#e5e5e5)] px-[6px] py-[3px] text-[10px] font-semibold text-[var(--fg-4,#a3a3a3)] @[280px]:inline">
              ⌘N
            </span>
          </button>
        </footer>
      )}

      {/* ── Settings panel ── */}
      <div className="px-5 pb-5">
        <SettingsPanel
          ringThickness={ringThickness}
          showLegend={showLegend}
          legendPlacement={legendPlacement}
          onThicknessChange={onThicknessChange}
          onLegendChange={onLegendChange}
          onPlacementChange={onPlacementChange}
        />
      </div>
    </div>
  );
};
